import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { getHelpDocsJwt } from "src/features/auth/state/authSlice";
import { formatContentTypeName } from "../../util/NameFormatter";
import type { HelpArticle } from "./useHelpArticle";
import "./helpArticleBody.scss";

/**
 * Build the authenticated help-center deep link exactly like
 * generateHelpDocsUrl() in OnboardingTour.tsx: sign in via the HelpDocs SSO JWT
 * and forward to the article's path. Falls back to the raw public article URL
 * when the JWT is missing (user then hits the standard KB login).
 */
const buildHelpCenterHref = (article: HelpArticle, helpDocsJwt: string): string => {
  if (!helpDocsJwt) return article.url;
  // Keep the #section anchor (article.url may end in e.g. "#audio") on the
  // forwarded path so the SSO redirect lands on the right H5 — pathname alone
  // drops the hash.
  const parsed = new URL(article.url);
  const url = new URL("https://wixar.helpdocs.io/login/jwt/token");
  url.searchParams.set("hd_jwt", helpDocsJwt);
  url.searchParams.set("forward", parsed.pathname + parsed.hash);
  return url.toString();
};

interface HelpArticlePanelProps {
  modelName: string;
  article: HelpArticle | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Right-side help drawer for the node edit form. Reuses the visual shell of the
 * graph "issues" panel (opaque white, fixed to the right edge, no backdrop,
 * close via ×) rather than the generic SlideOver, matching the slider the
 * feat-improve-graph-capabilities branch introduced. Content comes from the
 * mapped HelpDocs article (fetched by useHelpArticle in NodeForm); the (?)
 * trigger next to the "Éditer: …" title only appears once the article is ready,
 * so `article` is populated by the time this opens.
 */
const HelpArticlePanel = ({ modelName, article, open, onClose }: HelpArticlePanelProps) => {
  const { t } = useTranslation();
  const helpDocsJwt = useAppSelector(getHelpDocsJwt);

  // Esc closes the drawer — there is no backdrop to click (issues-panel parity).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !article) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-[500] w-96 bg-white border-l border-gray-200 shadow-lg flex flex-col">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <p className="text-sm font-semibold text-gray-700">
          {t("pages.project.editNode.helpSectionTitle", {
            name: formatContentTypeName(modelName),
          })}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
          aria-label={t("general.close")}
        >
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div
          className="help-article-body text-sm text-gray-700"
          // body is sanitized server-side (isomorphic-dompurify) in the
          // help-article proxy — see docs/plans/todo/helpdocs-inline-help.md D3
          dangerouslySetInnerHTML={{ __html: article.body }}
        />
        <a
          className="font-bold text-green-500 underline underline-offset-4 inline-block mt-4 text-sm"
          target="_blank"
          rel="noopener noreferrer"
          href={buildHelpCenterHref(article, helpDocsJwt)}
        >
          {t("pages.project.editNode.helpSectionOpenInHelpCenter")}
        </a>
      </div>
    </div>
  );
};

export default HelpArticlePanel;
